import _ from 'lodash'
import { ReportTableRow } from '../../../../components/Table/types'
import { Investment } from '../../../../types/investment/Investment'
import { Purpose } from '../../../../types/purpose/Purpose'
import { parseDate, formatAsDate } from '../../../../config/dayjsConfig'
import { EnhancedStatementRow } from '../../../financialStatements/utils/dataEnhancement'

export const debtFundingSourceIds = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17, 18, 19, 20, 24, 25, 26, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 101,
  102, 103, 104, 105, 106, 107, 108, 109, 110
]

export const investmentFormFormat = (values: Investment) => {
  const formattedData = {
    ...values,
    date: parseDate(values?.date),
    deprecationPlan: {
      ...values?.deprecationPlan,
      startDate: parseDate(values?.deprecationPlan?.startDate)
    },
    taxDeprecationPlan: {
      ...values?.taxDeprecationPlan,
      startDate: parseDate(values?.taxDeprecationPlan?.startDate)
    },
    ...(values.fundings && {
      fundings: values.fundings.map((investmentFunding: any) => {
        let fundingFormattedData = {
          ...investmentFunding
        }
        if (investmentFunding.loan) {
          fundingFormattedData = {
            ...fundingFormattedData,
            loan: {
              ...investmentFunding.loan,
              date: parseDate(investmentFunding?.loan?.date),
              nextInstalmentDate: parseDate(investmentFunding?.loan?.nextInstalmentDate),
              purposes: investmentFunding.loan.purposes
                ? investmentFunding.loan.purposes.map((p: Purpose) => p.name)
                : [],
              termsAndRepaymentsSame: !!(
                investmentFunding.loan.termYears === investmentFunding.loan.repaymentProgramYears &&
                investmentFunding.loan.termMonths === investmentFunding.loan.repaymentProgramMonths
              )
            }
          }
        }
        return fundingFormattedData
      })
    })
  }

  return formattedData
}

export const investmentSaveFormat = (values: any) => {
  return {
    ...values,
    date: typeof values?.date === 'string' ? values.date : formatAsDate(values?.date),
    ...(values.deprecationPlan && {
      deprecationPlan: {
        ...values.deprecationPlan,
        startDate:
          typeof values?.deprecationPlan?.startDate === 'string'
            ? values.deprecationPlan.startDate
            : formatAsDate(values?.deprecationPlan?.startDate)
      }
    }),
    ...(values.taxDeprecationPlan && {
      taxDeprecationPlan: {
        ...values?.taxDeprecationPlan,
        startDate:
          typeof values?.taxDeprecationPlan?.startDate === 'string'
            ? values.taxDeprecationPlan.startDate
            : formatAsDate(values?.taxDeprecationPlan?.startDate)
      }
    }),
    ...(values.fundings && {
      fundings: values.fundings.map((investmentFunding: any) => {
        let fundingFormattedData = {
          ...investmentFunding,
          investmentFundingSource: undefined
        }
        if (investmentFunding.loan && debtFundingSourceIds.includes(fundingFormattedData.investmentFundingSourceId)) {
          fundingFormattedData = {
            ...fundingFormattedData,
            loan: {
              ...investmentFunding.loan,
              purposes: investmentFunding.loan.purposes
                ? investmentFunding.loan.purposes.map((p: string) => ({ name: p }))
                : []
            }
          }
        } else {
          fundingFormattedData = {
            ...fundingFormattedData,
            loan: undefined,
            loanId: undefined
          }
        }
        return fundingFormattedData
      })
    })
  }
}

export const getInitialValues = (investment?: Investment, reportTableRow?: ReportTableRow | EnhancedStatementRow) => {
  let initValues: any = _.cloneDeep(investment)
  if (initValues) {
    initValues = {
      ...initValues,
      date: parseDate(initValues.date),
      nextInstalmentDate: parseDate(initValues.nextInstalmentDate),
      interestRate: initValues.interestRate ? Number(initValues.interestRate) * 100 : 0,
      vat: initValues.vat ? Math.round(Number(initValues.vat) * 100) : 0
    }
  }
  if (initValues && initValues.deprecationPlan) {
    initValues.deprecationPlan = {
      ...initValues.deprecationPlan,
      // deprecationMethodId: initValues.deprecationPlan.deprecationMethodId,
      startDate: parseDate(initValues.deprecationPlan.startDate)
    }
  }
  if (initValues && initValues.taxDeprecationPlan) {
    initValues.taxDeprecationPlan = {
      ...initValues.taxDeprecationPlan,
      startDate: parseDate(initValues.taxDeprecationPlan.startDate)
    }
  }
  if (initValues && initValues.fundings) {
    initValues.fundings = initValues.fundings.map((investmentFunding: any) => {
      let fundingFormattedData = {
        ...investmentFunding
      }
      if (investmentFunding.loan) {
        fundingFormattedData = {
          ...fundingFormattedData,
          loan: {
            ...investmentFunding.loan,
            nextInstalmentDate: parseDate(investmentFunding.loan.nextInstalmentDate),
            purposes: investmentFunding.loan.purposes
              ? investmentFunding.loan.purposes.map((p: Purpose) => p.name)
              : [],
            termsAndRepaymentsSame: !!(
              investmentFunding.loan.termYears === investmentFunding.loan.repaymentProgramYears &&
              investmentFunding.loan.termMonths === investmentFunding.loan.repaymentProgramMonths
            )
          }
        }
      }
      return fundingFormattedData
    })
  }
  if (!initValues && reportTableRow) {
    // Check if it's EnhancedStatementRow (has _sourceAccountCode field)
    const isEnhancedRow = '_sourceAccountCode' in reportTableRow

    if (isEnhancedRow) {
      // EnhancedStatementRow from financial statements grid
      const enhancedRow = reportTableRow as EnhancedStatementRow
      initValues = {
        accountCode: enhancedRow._sourceAccountCode,
        // balanceSheetRowId: enhancedRow._sourceStatementRowId,
        deprecationPlan: {
          deprecationMethodId: 1
        }
      }
    } else {
      // Original ReportTableRow from other contexts
      const reportRow = reportTableRow as ReportTableRow
      if (reportRow.id) {
        initValues = {
          balanceSheetRowId: reportRow.id,
          deprecationPlan: {
            deprecationMethodId: 1
          }
        }
      } else if (reportRow.code) {
        initValues = {
          accountCode: reportRow.code,
          deprecationPlan: {
            deprecationMethodId: 1
          }
        }
      }
    }
  }

  return initValues
}
